import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, User, Phone, DollarSign, Target, Calendar, CheckCircle, XCircle, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCommerciaux = () => {
  const [commerciaux, setCommerciaux] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    poste: '',
    dateRecrutement: new Date().toISOString().split('T')[0],
    dateDebutSuiviCA: '',
    objectifCA: '',
    objectifJournalier: '',
    salaireFix: '',
    salaireBrut: '',
    telephone: '',
    photoBase64: '',
    actif: true
  });

  useEffect(() => {
    fetchCommerciaux();
  }, []);

  const fetchCommerciaux = async () => {
    try {
      const response = await axios.get(`${API}/commerciaux`, { withCredentials: true });
      setCommerciaux(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    }
  };

  const handleOpenDialog = (commercial = null) => {
    if (commercial) {
      setIsEditing(true);
      setSelectedCommercial(commercial);
      setFormData({
        nom: commercial.nom,
        poste: commercial.poste || '',
        dateRecrutement: new Date(commercial.dateRecrutement).toISOString().split('T')[0],
        dateDebutSuiviCA: commercial.dateDebutSuiviCA || '',
        objectifCA: commercial.objectifCA?.toString() || '',
        objectifJournalier: commercial.objectifJournalier?.toString() || '',
        salaireFix: commercial.salaireFix?.toString() || '',
        salaireBrut: commercial.salaireBrut?.toString() || commercial.salaire?.toString() || '',
        telephone: commercial.telephone || '',
        photoBase64: commercial.photoBase64 || '',
        actif: commercial.actif
      });
    } else {
      setIsEditing(false);
      setSelectedCommercial(null);
      setFormData({
        nom: '',
        poste: '',
        dateRecrutement: new Date().toISOString().split('T')[0],
        dateDebutSuiviCA: '',
        objectifCA: '',
        objectifJournalier: '',
        salaireFix: '',
        salaireBrut: '',
        telephone: '',
        photoBase64: '',
        actif: true
      });
    }
    setShowDialog(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photoBase64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = {
        ...formData,
        dateDebutSuiviCA: formData.dateDebutSuiviCA || null,
        objectifCA: parseInt(formData.objectifCA),
        objectifJournalier: parseInt(formData.objectifJournalier) || 0,
        salaireFix: parseInt(formData.salaireFix),
        salaireBrut: parseInt(formData.salaireBrut)
      };

      if (isEditing && selectedCommercial) {
        await axios.put(`${API}/commerciaux/${selectedCommercial.id}`, dataToSend, { withCredentials: true });
        toast.success('Commercial modifié');
      } else {
        await axios.post(`${API}/commerciaux`, dataToSend, { withCredentials: true });
        toast.success('Commercial ajouté');
      }

      setShowDialog(false);
      fetchCommerciaux();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const toggleActif = async (commercial) => {
    try {
      await axios.put(`${API}/commerciaux/${commercial.id}`, {
        actif: !commercial.actif
      }, { withCredentials: true });
      toast.success(`Commercial ${!commercial.actif ? 'activé' : 'désactivé'}`);
      fetchCommerciaux();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (commercial) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${commercial.nom} ?`)) return;

    try {
      await axios.delete(`${API}/commerciaux/${commercial.id}`, { withCredentials: true });
      toast.success('Commercial supprimé');
      fetchCommerciaux();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleInitCommerciaux = async () => {
    if (!window.confirm('Cela va réinitialiser Emmanuel ZIRE et Gisèle KONAN. Continuer ?')) return;

    try {
      const response = await axios.post(`${API}/init-commerciaux`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Commerciaux initialisés avec succès');
      fetchCommerciaux();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation');
    }
  };

  const handleMigrateTransactions = async () => {
    if (commerciaux.length === 0) {
      toast.error('Veuillez d\'abord initialiser les commerciaux');
      return;
    }

    // Use first active commercial as default
    const defaultCommercial = commerciaux.find(c => c.actif);
    if (!defaultCommercial) {
      toast.error('Aucun commercial actif trouvé');
      return;
    }

    if (!window.confirm(`Cela va attribuer TOUTES les anciennes transactions à ${defaultCommercial.nom}. Continuer ?`)) return;

    try {
      const response = await axios.post(`${API}/transactions/migrate/add-commercialid`, {
        defaultCommercialId: defaultCommercial.id
      }, { withCredentials: true });
      
      toast.success(`✅ Migration réussie ! ${response.data.transactionsUpdated} transactions et ${response.data.tournamentsUpdated} tournois migrés vers ${response.data.commercialName}`);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la migration');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#C6A15B]">Gestion des Commerciaux</h2>
          <p className="text-[#EAEAEA]/60 text-sm mt-1">Gérez votre équipe commerciale</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleMigrateTransactions}
            className="bg-[#10B981] hover:bg-[#059669] text-white font-bold"
          >
            🔄 Migrer les Transactions
          </Button>
          <Button
            onClick={handleInitCommerciaux}
            className="bg-[#1C2541] hover:bg-[#2A3B5C] text-[#C6A15B] border-2 border-[#C6A15B]/40"
          >
            Initialiser Emmanuel & Gisèle
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-[#C6A15B] to-[#FFD700] hover:from-[#FFD700] hover:to-[#C6A15B] text-[#0B132B] font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un Commercial
          </Button>
        </div>
      </div>

      {/* Liste des commerciaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {commerciaux.map((commercial) => (
          <Card key={commercial.id} className={`bg-[#1C2541] border-2 ${commercial.actif ? 'border-[#C6A15B]/40' : 'border-gray-600/40'}`}>
            <CardContent className="p-6">
              {/* Photo et nom */}
              <div className="flex items-center gap-4 mb-4">
                {commercial.photoBase64 ? (
                  <img 
                    src={commercial.photoBase64} 
                    alt={commercial.nom}
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#C6A15B]"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C6A15B] to-[#FFD700] flex items-center justify-center">
                    <User className="w-8 h-8 text-[#0B132B]" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#EAEAEA] break-words">{commercial.nom}</h3>
                  {commercial.poste && (
                    <p className="text-[#C6A15B] text-sm font-medium mt-0.5">{commercial.poste}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {commercial.actif ? (
                      <span className="text-xs bg-[#10B981]/20 text-[#10B981] px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Actif
                      </span>
                    ) : (
                      <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Inactif
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Informations */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#EAEAEA]/80">
                  <Target className="w-4 h-4 text-[#C6A15B]" />
                  Objectif: <span className="font-bold text-[#FFD700]">{(commercial.objectifCA || 0).toLocaleString()} F</span>
                </div>
                <div className="flex items-center gap-2 text-[#EAEAEA]/80">
                  <DollarSign className="w-4 h-4 text-[#10B981]" />
                  Fixe: <span className="font-bold text-[#10B981]">{(commercial.salaireFix || 0).toLocaleString()} F</span>
                </div>
                <div className="flex items-center gap-2 text-[#EAEAEA]/80">
                  <DollarSign className="w-4 h-4 text-[#C6A15B]" />
                  Brut: <span className="font-bold text-[#C6A15B]">{(commercial.salaireBrut || commercial.salaire || 0).toLocaleString()} F</span>
                </div>
                {commercial.telephone && (
                  <div className="flex items-center gap-2 text-[#EAEAEA]/80">
                    <Phone className="w-4 h-4 text-[#C6A15B]" />
                    {commercial.telephone}
                  </div>
                )}
                {commercial.dateRecrutement && (
                  <div className="flex items-center gap-2 text-[#EAEAEA]/80">
                    <Calendar className="w-4 h-4 text-[#C6A15B]" />
                    Depuis: {new Date(commercial.dateRecrutement).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => handleOpenDialog(commercial)}
                  size="sm"
                  className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B] text-[#EAEAEA] hover:text-[#0B132B]"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  onClick={() => toggleActif(commercial)}
                  size="sm"
                  variant="outline"
                  className={`border-2 ${commercial.actif ? 'border-[#10B981] text-[#10B981]' : 'border-gray-500 text-gray-400'}`}
                >
                  {commercial.actif ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={() => handleDelete(commercial)}
                  size="sm"
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {commerciaux.length === 0 && (
        <div className="text-center py-12 text-[#EAEAEA]/40">
          <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>Aucun commercial configuré</p>
        </div>
      )}

      {/* Dialog Ajout/Modification */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B] flex items-center gap-2">
              <User className="w-5 h-5" />
              {isEditing ? 'Modifier le Commercial' : 'Ajouter un Commercial'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nom Complet *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                required
              />
            </div>

            <div>
              <Label>Poste</Label>
              <Input
                value={formData.poste}
                onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                placeholder="Ex: Commercial Senior, Vendeur..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de Recrutement *</Label>
                <Input
                  type="date"
                  value={formData.dateRecrutement}
                  onChange={(e) => setFormData({ ...formData, dateRecrutement: e.target.value })}
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  required
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  placeholder="+225..."
                />
              </div>
            </div>

            <div>
              <Label>Date début suivi CA <span className="text-[#EAEAEA]/50 font-normal text-xs">(optionnel — laissez vide pour démarrer dès le 1er du mois)</span></Label>
              <Input
                type="date"
                value={formData.dateDebutSuiviCA}
                onChange={(e) => setFormData({ ...formData, dateDebutSuiviCA: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                placeholder="ex. date de démarrage du commercial"
                data-testid="input-date-debut-suivi-ca"
              />
              <p className="text-[10px] text-[#EAEAEA]/50 mt-1 italic">
                Avant cette date, aucun objectif journalier ne s'applique. Aucun retard cumulé n'est imputé pour les jours antérieurs.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Objectif CA Mensuel (FCFA) *</Label>
                <Input
                  type="number"
                  value={formData.objectifCA}
                  onChange={(e) => setFormData({ ...formData, objectifCA: e.target.value })}
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  placeholder="300000"
                  required
                />
              </div>
              <div>
                <Label>Objectif CA Journalier (FCFA)</Label>
                <Input
                  data-testid="commercial-objectif-journalier"
                  type="number"
                  step="500"
                  min="0"
                  value={formData.objectifJournalier}
                  onChange={(e) => setFormData({ ...formData, objectifJournalier: e.target.value })}
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA]"
                  placeholder="15000"
                />
                <p className="text-xs text-[#FFD700]/60 mt-1">Multiple de 500 · Lun-Sam · 0 = non défini</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Salaire Fixe (FCFA) *</Label>
                <Input
                  type="number"
                  value={formData.salaireFix}
                  onChange={(e) => setFormData({ ...formData, salaireFix: e.target.value })}
                  className="bg-[#0B132B] border-[#10B981]/30 text-[#EAEAEA]"
                  placeholder="60000"
                  required
                />
                <p className="text-xs text-[#10B981]/60 mt-1">Minimum garanti</p>
              </div>
            </div>

            <div>
              <Label>Salaire Brut (FCFA) *</Label>
              <Input
                type="number"
                value={formData.salaireBrut}
                onChange={(e) => setFormData({ ...formData, salaireBrut: e.target.value })}
                className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                placeholder="110000"
                required
              />
              <p className="text-xs text-[#C6A15B]/60 mt-1">Maximum à 100% objectif</p>
            </div>

            <div>
              <Label>Photo (optionnel)</Label>
              <div className="flex items-center gap-4">
                {formData.photoBase64 && (
                  <img src={formData.photoBase64} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.actif}
                onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                className="w-4 h-4"
              />
              <Label>Commercial actif</Label>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setShowDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#2A3B5C] text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-[#C6A15B] to-[#FFD700] hover:from-[#FFD700] hover:to-[#C6A15B] text-[#0B132B] font-bold"
              >
                {isEditing ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommerciaux;
